export type AppointmentStatusDisplay = {
  label: string
  color: string
}

export const APPOINTMENT_STATUS_CANCELED = 6

export function isAppointmentCancelled(status?: number | null): boolean {
  return status === APPOINTMENT_STATUS_CANCELED
}

const STATUS_NAME_MAP: Record<string, AppointmentStatusDisplay> = {
  Open: {label: 'Open', color: 'rgba(215, 56, 60, 1)'},
  ReadyToAssign: {label: 'Ready To Assign', color: 'rgba(163, 162, 66, 1)'},
  Confirmed: {label: 'Confirmed', color: 'rgba(96, 187, 26, 1)'},
  NoInterpreter: {label: 'No Interpreter', color: 'rgba(255, 199, 0, 1)'},
  Abandoned: {label: 'Abandoned', color: 'rgba(227, 117, 155, 1)'},
  Completed: {label: 'Completed', color: 'rgba(24, 28, 50, 1)'},
  Canceled: {label: 'Canceled', color: 'rgba(228, 230, 239, 1)'},
  Submitted: {label: 'Submitted', color: 'rgba(24, 28, 50, 1)'},
  InProgress: {label: 'In Progress', color: 'rgba(74, 198, 215, 1)'},
  CheckedIn: {label: 'Checked In', color: 'rgba(74, 198, 215, 1)'},
  CheckedOut: {label: 'Checked Out', color: 'rgba(74, 198, 215, 1)'},
}

const UNKNOWN_STATUS: AppointmentStatusDisplay = {
  label: 'Unknown',
  color: 'rgba(128, 128, 128, 1)',
}

const STATUS_CODE_TO_NAME: Record<number, string> = {
  0: 'Open',
  1: 'ReadyToAssign',
  2: 'Confirmed',
  3: 'NoInterpreter',
  4: 'Abandoned',
  5: 'Completed',
  6: 'Canceled',
  7: 'Submitted',
  8: 'InProgress',
  9: 'CheckedIn',
  10: 'CheckedOut',
}

export function getAppointmentStatusByName(statusName?: string | null): AppointmentStatusDisplay {
  if (!statusName) return UNKNOWN_STATUS
  return STATUS_NAME_MAP[statusName] ?? UNKNOWN_STATUS
}

export function getAppointmentStatusByCode(status?: number | null): AppointmentStatusDisplay {
  if (status === undefined || status === null) return UNKNOWN_STATUS
  const statusName = STATUS_CODE_TO_NAME[status]
  return statusName ? getAppointmentStatusByName(statusName) : UNKNOWN_STATUS
}

export function getAppointmentStatusCalendarStyle(status: number): {
  color: string
  textColor?: string
} {
  switch (status) {
    case 0:
      return {color: 'rgba(215, 56, 60, 0.7)'}
    case 1:
      return {color: 'rgba(163, 162, 66, 0.7)'}
    case 2:
      return {color: 'rgba(96, 187, 26, 0.7)'}
    case 3:
      return {color: 'rgba(255, 199, 0, 0.7)'}
    case 4:
      return {color: 'rgba(227, 117, 155, 0.7)'}
    case 5:
      return {color: 'rgba(24, 28, 50, 0.7)'}
    case 6:
      return {color: 'rgba(228, 230, 239, 0.7)', textColor: 'black'}
    case 7:
      return {color: '#346beb'}
    case 8:
      return {color: 'rgba(74, 198, 215, 0.7)'}
    case 9:
      return {color: 'rgba(45, 170, 190, 0.85)'}
    case 10:
      return {color: 'rgba(24, 140, 160, 0.85)'}
    default:
      return {color: ''}
  }
}

export function getAppointmentStatusBorderClass(
  status: number,
  userType: string | undefined
): string | undefined {
  if (userType === 'SYSTEM' || userType === 'INTERPRETER') {
    if (status === 0) return 'border-danger'
    if (status === 1) return 'border-primary'
    if (status === 2) return 'border-success'
    if (status === 3) return 'border-warning'
    if (status === 4) return 'border-info'
    if (status === 5) return 'border-dark'
    if (status === 6) return 'border-muted'
    if (status === 7) return 'border-custom-blue'
    if (status === 8) return 'border-custom-inprogress'
    if (status === 9) return 'border-custom-checkedin'
    if (status === 10) return 'border-custom-checkedout'
  } else if (userType === 'CONSUMER') {
    if (status === 0 || status === 1) return 'border-danger'
    if (status === 2) return 'border-success'
    if (status === 3) return 'border-warning'
    if (status === 4) return 'border-info'
    if (status === 5) return 'border-dark'
    if (status === 6) return 'border-muted'
    if (status === 7) return 'border-custom-blue'
    if (status === 8) return 'border-custom-inprogress'
    if (status === 9) return 'border-custom-checkedin'
    if (status === 10) return 'border-custom-checkedout'
  }
  return undefined
}

export const APPOINTMENT_STATUS_FILTER_OPTIONS = [
  {value: '0', label: 'Open'},
  {value: '1', label: 'Ready to Assign'},
  {value: '2', label: 'Confirmed'},
  {value: '3', label: 'No interpreter'},
  {value: '4', label: 'Abandoned'},
  {value: '5', label: 'Complete'},
  {value: '6', label: 'Cancelled'},
  {value: '7', label: 'Submitted'},
  {value: '8', label: 'In Progress'},
  {value: '9', label: 'Checked In'},
  {value: '10', label: 'Checked Out'},
  {value: 'billable', label: 'Billable'},
  {value: 'payable', label: 'Payable'},
] as const

export const APPOINTMENT_STATUS_SUMMARY_CHIP_COLORS = {
  checkedIn: 'rgba(45, 170, 190, 1)',
  inProgress: 'rgba(74, 198, 215, 1)',
  submitted: '#346beb',
} as const

export type MeetingSummaryTabAppointment = {
  status?: number
  payable?: boolean
  billable?: boolean
  communicationTypeIdentification?: string
}

/** On-site / third-party appointments where the Meeting Summary tab is shown. */
export function isMeetingSummaryTabVisible(
  appointment: MeetingSummaryTabAppointment | null | undefined
): boolean {
  if (!appointment) return false
  const comm = appointment.communicationTypeIdentification
  if (comm !== 'ON_SITE' && comm !== 'THIRD_PART_PLATFORM') return false

  const status = appointment.status
  if (status === 2 || status === 5 || status === 7 || status === 9 || status === 10) {
    return true
  }
  if (status === 6 && (appointment.payable || appointment.billable)) {
    return true
  }
  return false
}

const INTERPRETER_ASSIGNED_STATUSES = new Set<number>([2, 5, 7, 8, 9, 10])

export function isInterpreterAssigned(status: number | null | undefined): boolean {
  if (status === null || status === undefined) return false
  return INTERPRETER_ASSIGNED_STATUSES.has(status)
}

const CONSUMER_APPOINTMENT_STATUS_FILTER_VALUES = [
  '0',
  '2',
  '9',
  '7',
  '5',
  '6',
  '4',
] as const

function appointmentStatusFilterOptionsForValues(
  values: readonly string[]
): (typeof APPOINTMENT_STATUS_FILTER_OPTIONS)[number][] {
  return values
    .map((value) => APPOINTMENT_STATUS_FILTER_OPTIONS.find((option) => option.value === value))
    .filter((option): option is (typeof APPOINTMENT_STATUS_FILTER_OPTIONS)[number] => option != null)
}

export function getFilteredAppointmentStatusOptions(userType: string | undefined) {
  if (userType === 'INTERPRETER') {
    return appointmentStatusFilterOptionsForValues(['0', '2', '4', '5', '7', '8', '9', '10'])
  }
  if (userType === 'SYSTEM') {
    return appointmentStatusFilterOptionsForValues([
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
    ])
  }
  if (userType === 'CONSUMER') {
    return appointmentStatusFilterOptionsForValues(CONSUMER_APPOINTMENT_STATUS_FILTER_VALUES)
  }
  return [...APPOINTMENT_STATUS_FILTER_OPTIONS]
}
